/**
 * Teleredo Design System — Semantic Token Layer
 *
 * Brand palette: Primary Purple #7A3FF2 + Teal #00C8C8.
 * Raw values live here; neu.js holds the shadow/surface primitives.
 * Components consume these tokens, never raw hex values directly.
 *
 * THEMING: Replace BRAND and TEAL objects to white-label for a reseller.
 */

// ─── Raw palette ──────────────────────────────────────────────────────────────

export const BRAND_PALETTE = {
  50:  "#EEF2FF",
  100: "#E0E7FF",
  200: "#C7D2FE",
  300: "#A5B4FC",
  400: "#818CF8",
  500: "#6366F1",   // brand-light
  600: "#4F46E5",   // brand (primary)
  700: "#4338CA",   // brand-dark
  800: "#3730A3",
  900: "#312E81",
};

export const TEAL_PALETTE = {
  50:  "#ECFEFF",
  100: "#CFFAFE",
  200: "#A5F3FC",
  300: "#67E8F9",
  400: "#22D3EE",
  500: "#06B6D4",   // accent (secondary)
  600: "#0891B2",
  700: "#0E7490",
  800: "#155E75",
  900: "#164E63",
};

// ─── Semantic color tokens ─────────────────────────────────────────────────────

export const COLOR = {
  // Brand
  brandPrimary:      BRAND_PALETTE[600],    // #4F46E5
  brandLight:        BRAND_PALETTE[500],    // #6366F1
  brandDark:         BRAND_PALETTE[800],    // #3730A3
  brandSubtle:       "rgba(79,70,229,0.08)",

  // Secondary / accent
  tealPrimary:       TEAL_PALETTE[500],     // #06B6D4
  tealLight:         TEAL_PALETTE[400],     // #22D3EE
  tealDark:          TEAL_PALETTE[600],     // #0891B2
  tealSubtle:        "rgba(6,182,212,0.10)",

  // Semantic
  success:           "#22C55E",
  successSubtle:     "rgba(34,197,94,0.10)",
  warning:           "#F59E0B",
  warningSubtle:     "rgba(245,158,11,0.10)",
  danger:            "#EF4444",
  dangerSubtle:      "rgba(239,68,68,0.10)",
  info:              "#3B82F6",
  infoSubtle:        "rgba(59,130,246,0.10)",

  // Neutral
  neutral50:  "#F9FAFB",
  neutral100: "#F3F4F6",
  neutral200: "#E5E7EB",
  neutral300: "#D1D5DB",
  neutral400: "#9CA3AF",
  neutral500: "#6B7280",
  neutral600: "#4B5563",
  neutral700: "#374151",
  neutral800: "#1F2937",
  neutral900: "#111827",

  // Always white/black (don't theme these)
  white: "#FFFFFF",
  black: "#000000",
};

// ─── Call-center domain status colors ─────────────────────────────────────────
// Used by StatusPill, CDR tables, agent dashboard, live queue view.

export const CALL_STATUS = {
  ANSWERED:    { color: COLOR.success,      bg: COLOR.successSubtle,  label: "Answered",    icon: "call_received" },
  NOANSWER:    { color: COLOR.warning,      bg: COLOR.warningSubtle,  label: "No Answer",   icon: "call_missed"   },
  BUSY:        { color: COLOR.danger,       bg: COLOR.dangerSubtle,   label: "Busy",        icon: "call_end"      },
  FAILED:      { color: "#DC2626",          bg: "rgba(220,38,38,0.1)",label: "Failed",      icon: "error"         },
  TRANSFERRED: { color: COLOR.info,         bg: COLOR.infoSubtle,     label: "Transferred", icon: "forward"       },
  LIVE:        { color: COLOR.tealPrimary,  bg: COLOR.tealSubtle,     label: "Live",        icon: "phone_in_talk" },
  RECORDING:   { color: COLOR.danger,       bg: COLOR.dangerSubtle,   label: "Recording",   icon: "fiber_manual_record" },
  VOICEMAIL:   { color: COLOR.brandPrimary, bg: COLOR.brandSubtle,    label: "Voicemail",   icon: "voicemail"     },
};

export const AGENT_STATUS = {
  AVAILABLE: { color: COLOR.success,      bg: COLOR.successSubtle, label: "Available",  pulse: false },
  ON_CALL:   { color: COLOR.tealPrimary,  bg: COLOR.tealSubtle,    label: "On Call",    pulse: true  },
  AWAY:      { color: COLOR.warning,      bg: COLOR.warningSubtle, label: "Away",       pulse: false },
  OFFLINE:   { color: COLOR.neutral400,   bg: "rgba(156,163,175,0.10)", label: "Offline", pulse: false },
  DND:       { color: COLOR.danger,       bg: COLOR.dangerSubtle,  label: "Do Not Dist.", pulse: false },
};

export const SECTION_STATUS = {
  ACTIVE:        { color: COLOR.success,      bg: COLOR.successSubtle, label: "Active"        },
  INACTIVE:      { color: COLOR.neutral400,   bg: "rgba(156,163,175,0.10)", label: "Inactive" },
  UNCONFIGURED:  { color: COLOR.warning,      bg: COLOR.warningSubtle, label: "Unconfigured"  },
};

// ─── Spacing scale (4 px base) ────────────────────────────────────────────────

export const SPACE = {
  0:    "0px",
  0.5:  "2px",
  1:    "4px",
  1.5:  "6px",
  2:    "8px",
  2.5:  "10px",
  3:    "12px",
  4:    "16px",
  5:    "20px",
  6:    "24px",
  7:    "28px",
  8:    "32px",
  10:   "40px",
  12:   "48px",
  16:   "64px",
  20:   "80px",
  24:   "96px",
};

// Semantic aliases
export const SP = {
  xs:  SPACE[1],    // 4px
  sm:  SPACE[2],    // 8px
  md:  SPACE[4],    // 16px
  lg:  SPACE[6],    // 24px
  xl:  SPACE[8],    // 32px
  "2xl": SPACE[12], // 48px
  "3xl": SPACE[16], // 64px
};

// ─── Border radius ─────────────────────────────────────────────────────────────

export const RADIUS = {
  none: 0,
  xs:   4,
  sm:   8,
  md:   12,
  lg:   16,
  xl:   20,
  "2xl": 24,
  "3xl": 28,
  full: 9999,
};

// ─── Typography scale ──────────────────────────────────────────────────────────

export const FONT = {
  sans:  `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
  mono:  `'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace`,
};

export const TYPE = {
  display: { size: "2.5rem",    weight: 800, leading: 1.10, tracking: "-0.04em" },
  h1:      { size: "2rem",      weight: 700, leading: 1.15, tracking: "-0.03em" },
  h2:      { size: "1.75rem",   weight: 700, leading: 1.20, tracking: "-0.025em" },
  h3:      { size: "1.5rem",    weight: 700, leading: 1.25, tracking: "-0.02em" },
  h4:      { size: "1.25rem",   weight: 600, leading: 1.30, tracking: "-0.015em" },
  h5:      { size: "1.0625rem", weight: 600, leading: 1.40, tracking: "-0.01em" },
  h6:      { size: "0.9375rem", weight: 600, leading: 1.50, tracking: "0" },
  body:    { size: "0.9375rem", weight: 400, leading: 1.65, tracking: "-0.005em" },
  bodySm:  { size: "0.875rem",  weight: 400, leading: 1.60, tracking: "0" },
  caption: { size: "0.75rem",   weight: 400, leading: 1.50, tracking: "0" },
  label:   { size: "0.6875rem", weight: 700, leading: 1.40, tracking: "0.07em", upper: true },
  mono:    { size: "0.875rem",  weight: 500, leading: 1.50, family: FONT.mono },
  monoSm:  { size: "0.8125rem", weight: 500, leading: 1.40, family: FONT.mono },
};

// ─── Animation ────────────────────────────────────────────────────────────────

export const ANIM = {
  durationFast:   "120ms",
  durationNormal: "180ms",
  durationSlow:   "280ms",
  easeOut:        "cubic-bezier(0.16, 1, 0.3, 1)",
  easeIn:         "cubic-bezier(0.4, 0, 1, 1)",
  easeInOut:      "cubic-bezier(.2,.8,.2,1)",
  spring:         { type: "spring", stiffness: 400, damping: 22 },
  springBouncy:   { type: "spring", stiffness: 500, damping: 18 },
};

// ─── Z-index ──────────────────────────────────────────────────────────────────

export const Z = {
  behind:  -1,
  base:     0,
  raised:   1,
  dropdown: 10,
  sticky:   15,
  drawer:   20,
  modal:    30,
  toast:    40,
  tooltip:  50,
};

// ─── Density modes ────────────────────────────────────────────────────────────
// Operational: agents/supervisors — hours of use, data-dense, compact
// Guided:      admins/owners — config flows, generous spacing, guided UX

export const DENSITY = {
  operational: {
    cardPadding:  "12px 16px",
    sectionGap:   12,
    rowHeight:    40,
    cellPadding:  "6px 12px",
    bodySize:     TYPE.bodySm.size,
    captionSize:  "0.6875rem",
    iconSize:     18,
    buttonSize:   "small",
    inputSize:    "small",
  },
  guided: {
    cardPadding:  "24px",
    sectionGap:   24,
    rowHeight:    56,
    cellPadding:  "12px 16px",
    bodySize:     TYPE.body.size,
    captionSize:  "0.75rem",
    iconSize:     22,
    buttonSize:   "medium",
    inputSize:    "medium",
  },
};

// ─── Layout ───────────────────────────────────────────────────────────────────

export const BREAKPOINT = {
  xs:  0,
  sm:  600,
  md:  900,
  lg:  1200,
  xl:  1536,
};

export const CONTAINER = {
  sm:   "600px",
  md:   "900px",
  lg:   "1200px",
  xl:   "1440px",
  full: "100%",
};

// ─── Convenience sx helpers ────────────────────────────────────────────────────

export const truncate = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
export const monoSx   = { fontFamily: FONT.mono, fontVariantNumeric: "tabular-nums" };
export const labelSx  = {
  display: "block",
  fontSize: TYPE.label.size,
  fontWeight: TYPE.label.weight,
  letterSpacing: TYPE.label.tracking,
  textTransform: "uppercase",
  color: "text.disabled",
};
